import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat'

import { useQuery } from '@apollo/client'

import { getFragmentData } from '~publish/gql/fragment-masking'
import { stripGqlTypenames } from '~publish/helpers/stripGqlTypenames'
import useIdeasUploader from '~publish/hooks/useIdeasUploader'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { IdeaCard_Idea } from '~publish/pages/Create/components/IdeaCard/IdeaCard'

import type { Idea } from '../../pages/Create/types'
import { IdeaComposer } from '../IdeaComposer'
import { IdeaComposer as LegacyIdeaComposer } from '../LegacyIdeaComposer'
import { GetIdea } from './IdeaDetailsDialog'
import { useNewIdeaComposerSplit } from '../IdeaComposer/hooks'

/**
 * Component used to open IdeaComposer with existing idea.
 * Renders when corresponding route is matched.
 *
 * It will attempt to fetch the idea with the provided ideaId, returning it from cache first if available.
 */
export function IdeaEditDialog(): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const { isEnabled: isNewIdeaComposerEnabled } = useNewIdeaComposerSplit()

  const { ideaId } = useParams<{ ideaId: string; id: string }>()
  const [source] = useQueryParam('source')
  const { data, loading } = useQuery(GetIdea, {
    variables: {
      input: {
        id: ideaId ?? '',
      },
    },
    fetchPolicy: 'cache-first',
  })
  const uploader = useIdeasUploader()

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open)
    if (!open) {
      /**
       * When this modal is closed, we want to navigate back to the original page
       * that opened the composer to ensure a seamless experience.
       * The '..' path will navigate up one level in the route tree.
       * (This is based on the component routing levels, not literal path segments)
       *
       * Keeping the `search` (query params) is a bit of a hack, but it prevents
       * re-rendering the /create page, since without params the page will auto-add
       * the ?view=board and thus cause a flash/re-render + resetting scroll,
       * which is not what we want.
       */
      navigate({
        pathname: '..',
        search: location.search,
      })
    }
  }

  useEffect(() => {
    if (data?.idea) {
      setIsOpen(true)
    }
  }, [data?.idea, navigate, source])

  if (loading) {
    return <></>
  }

  if (data?.idea?.__typename !== 'Idea' || !data?.idea) {
    handleOpenChange(false)
    return null
  }

  const idea = getFragmentData(IdeaCard_Idea, data.idea) as Idea

  return isNewIdeaComposerEnabled ? (
    <IdeaComposer
      initialIdea={stripGqlTypenames(idea) ?? undefined}
      open={isOpen}
      onOpenChange={handleOpenChange}
      uploader={uploader}
    />
  ) : (
    <LegacyIdeaComposer
      initialIdea={stripGqlTypenames(idea) ?? undefined}
      open={isOpen}
      onOpenChange={handleOpenChange}
      uploader={uploader}
    />
  )
}
